import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { selectIsAuthenticated, selectCurrentUser } from '../../features/auth/authSlice';
import {
  useGetVerificationStatusQuery,
  useSubmitVerificationMutation,
} from '../../features/api/apiSlice';
import AgentLayout from '../agent-dashboard/AgentLayout';
import { isProfessionalRole } from '../../lib/authPortals';

const ROLE_OPTIONS = [
  { value: 'agent', label: 'Property Agent' },
  { value: 'broker', label: 'Broker' },
  { value: 'advisor', label: 'Property Advisor' },
];

const statusMeta = {
  none: { label: 'Not submitted', color: 'bg-muted text-foreground', icon: 'Shield' },
  pending: { label: 'Pending review', color: 'bg-warning/15 text-warning', icon: 'Clock' },
  under_review: { label: 'Under review', color: 'bg-primary/15 text-primary', icon: 'Search' },
  verified: { label: 'Verified', color: 'bg-success/15 text-success', icon: 'BadgeCheck' },
  rejected: { label: 'Rejected', color: 'bg-error/15 text-error', icon: 'XCircle' },
};

const VerificationPage = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const location = useLocation();
  const inAgentDashboard = location.pathname.startsWith('/agent-dashboard');
  const dashboardHome = isProfessionalRole(user?.account_role)
    ? '/agent-dashboard'
    : '/user-dashboard';
  const verificationPath = inAgentDashboard ? '/agent-dashboard/verification' : '/verification';

  const { data: statusData, isLoading, refetch } = useGetVerificationStatusQuery(undefined, {
    skip: !isAuthenticated,
  });
  const [submitVerification, { isLoading: isSubmitting }] = useSubmitVerificationMutation();

  const [form, setForm] = useState({
    role: 'broker',
    full_legal_name: user?.name || '',
    national_id_number: '',
    company_name: '',
    license_number: '',
    business_registration_number: '',
    years_experience: '',
    service_areas: '',
    notes: '',
  });
  const [files, setFiles] = useState({
    national_id_doc: null,
    license_doc: null,
    business_cert_doc: null,
    proof_of_address_doc: null,
    selfie_doc: null,
  });
  const [message, setMessage] = useState(location.state?.message || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: verificationPath } } });
    }
  }, [isAuthenticated, navigate, verificationPath]);

  useEffect(() => {
    if (statusData?.account_role && ['agent', 'broker', 'advisor'].includes(statusData.account_role)) {
      setForm((prev) => ({ ...prev, role: statusData.account_role }));
    }
    if (statusData?.company_name) {
      setForm((prev) => ({ ...prev, company_name: statusData.company_name || prev.company_name }));
    }
  }, [statusData]);

  const verificationStatus = statusData?.verification_status || 'none';
  const canSubmit =
    verificationStatus === 'none' ||
    verificationStatus === 'rejected' ||
    (!statusData?.latest_submission && verificationStatus !== 'verified');

  const handleFile = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!files.national_id_doc) {
      setError('National ID document is required.');
      return;
    }
    if (!form.full_legal_name.trim() || !form.national_id_number.trim()) {
      setError('Legal name and national ID number are required.');
      return;
    }

    const body = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v != null) body.append(k, v);
    });
    Object.entries(files).forEach(([k, file]) => {
      if (file) body.append(k, file);
    });

    try {
      const res = await submitVerification(body).unwrap();
      if (res?.error) {
        setError(res.message || 'Submission failed');
        return;
      }
      setMessage(res.message || 'Verification submitted successfully.');
      refetch();
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Submission failed. Please try again.');
    }
  };

  const meta = statusMeta[verificationStatus] || statusMeta.none;

  const content = (
    <>
      {!inAgentDashboard && (
        <div className="mb-8">
          <p className="text-sm text-primary font-medium mb-2">Trust · Verification</p>
          <h1 className="text-3xl font-bold text-foreground mb-2">Real Estate Agent Verification</h1>
          <p className="text-muted-foreground">
            Agents, brokers, and advisors build credibility by verifying identity and credentials.
            Verified real estate agents earn a trust badge and can respond to user needs on the notice board.
          </p>
        </div>
      )}

      <div className={`rounded-xl border border-border p-5 mb-8 flex items-start gap-4 ${meta.color}`}>
        <Icon name={meta.icon} size={28} />
        <div>
          <p className="font-semibold text-lg">{meta.label}</p>
          {statusData?.latest_submission?.admin_notes && verificationStatus === 'rejected' && (
            <p className="text-sm mt-1 opacity-90">
              Reviewer notes: {statusData.latest_submission.admin_notes}
            </p>
          )}
          {verificationStatus === 'verified' && (
            <p className="text-sm mt-1">
              Verified as {statusData?.account_role}. Your profile shows a trusted badge to users.
            </p>
          )}
        </div>
      </div>

      {message && (
        <div className="mb-6 p-4 rounded-lg bg-success/10 border border-success/20 text-success text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading verification status…</div>
      ) : canSubmit ? (
        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-card border border-border rounded-xl p-6"
        >
          <h2 className="text-xl font-semibold">Submit documents</h2>
          <Select
            label="Role"
            options={ROLE_OPTIONS}
            value={form.role}
            onChange={(v) => setForm((p) => ({ ...p, role: v }))}
            required
          />
          <Input
            label="Full legal name"
            value={form.full_legal_name}
            onChange={(e) => setForm((p) => ({ ...p, full_legal_name: e.target.value }))}
            required
          />
          <Input
            label="National ID number"
            value={form.national_id_number}
            onChange={(e) => setForm((p) => ({ ...p, national_id_number: e.target.value }))}
            required
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Company / agency"
              value={form.company_name}
              onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
            />
            <Input
              label="License number"
              value={form.license_number}
              onChange={(e) => setForm((p) => ({ ...p, license_number: e.target.value }))}
            />
            <Input
              label="Business registration no."
              value={form.business_registration_number}
              onChange={(e) =>
                setForm((p) => ({ ...p, business_registration_number: e.target.value }))
              }
            />
            <Input
              label="Years of experience"
              type="number"
              min="0"
              value={form.years_experience}
              onChange={(e) => setForm((p) => ({ ...p, years_experience: e.target.value }))}
            />
          </div>
          <Input
            label="Service areas"
            placeholder="e.g. Kampala, Wakiso, Entebbe"
            value={form.service_areas}
            onChange={(e) => setForm((p) => ({ ...p, service_areas: e.target.value }))}
          />
          <div>
            <label className="block text-sm font-medium mb-1">Additional notes</label>
            <textarea
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[90px]"
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Anything our review team should know"
            />
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-sm font-medium">Documents (PDF or image, max 5MB each)</p>
            {[
              { key: 'national_id_doc', label: 'National ID (required)', required: true },
              { key: 'license_doc', label: 'License document' },
              { key: 'business_cert_doc', label: 'Business certificate' },
              { key: 'proof_of_address_doc', label: 'Proof of address' },
              { key: 'selfie_doc', label: 'Selfie holding ID' },
            ].map((doc) => (
              <div key={doc.key}>
                <label className="block text-sm text-muted-foreground mb-1">
                  {doc.label}
                  {doc.required ? ' *' : ''}
                </label>
                <input
                  type="file"
                  accept={doc.key === 'selfie_doc' ? 'image/*' : 'image/*,.pdf'}
                  onChange={(e) => handleFile(doc.key, e.target.files?.[0] || null)}
                  className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground"
                  required={doc.required}
                />
              </div>
            ))}
          </div>

          <Button type="submit" fullWidth loading={isSubmitting} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit for verification'}
          </Button>
        </form>
      ) : (
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <p className="text-muted-foreground">
            {verificationStatus === 'verified'
              ? 'Your Real Estate Agent account is verified. No further action is needed.'
              : 'Your documents are with our team. We will notify you once review is complete.'}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to="/notice-board">Open notice board</Link>
            </Button>
            <Button asChild variant="default">
              <Link to={dashboardHome}>Go to dashboard</Link>
            </Button>
            {isProfessionalRole(user?.account_role) && (
              <Button asChild variant="outline">
                <Link
                  to={
                    inAgentDashboard ? '/agent-dashboard/profile' : '/user-profile'
                  }
                >
                  View profile
                </Link>
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );

  if (inAgentDashboard) {
    return (
      <AgentLayout
        title="Verification"
        subtitle="Earn your verified agent badge and build trust with clients."
      >
        <div className="max-w-3xl">{content}</div>
      </AgentLayout>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10">{content}</main>
      <Footer />
    </div>
  );
};

export default VerificationPage;
